import { ToastButton, useIonRouter, useIonToast } from '@ionic/react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { t } from 'ttag';

import RoutePath from '@custom-types/RoutePath';
import { generateRoute } from '@services/navigation-service';
import { getEventInfo } from '@state/event';
import { getKioskEnabled } from '@state/kiosk';
import type { Printer, PrintJobOptions } from '~capacitor-plugins/BadgePrinter';
import { useFindBluetoothPrinters } from '~capacitor-plugins/BadgePrinter';

import { usePrinterSettings } from './usePrinterSettings';
import { usePrintingEnabled } from './usePrintingEnabled';

export interface UsePrinterResult {
  /** Is printing enabled in this event and with this device? */
  enabled: boolean;
  /** Is the list of printers and printer settings still loading? */
  isLoading: boolean;
  /** Is the list of printers still loading? */
  isLoadingPrinters: boolean;
  /** Are the settings still loading? */
  isLoadingSettings: boolean;
  /** A list of the printers that are known by this device */
  availablePrinters: Printer[];
  /** The printer that is selected in the settings, if it is currently available */
  selectedPrinter?: Printer;
  /** Should an error be shown about the selected printer being unavailable? */
  needsSetup: boolean;
  /** The persisted settings for badge printing */
  settings: PrintJobOptions;
  /** Save and persist the settings for badge printing */
  saveSettings: (settings: PrintJobOptions) => void;
}

export const usePrinter = (): UsePrinterResult => {
  const enabled = usePrintingEnabled();
  const { data: printers, isLoading: isLoadingPrinters } = useFindBluetoothPrinters();
  const { settings, saveSettings, isLoading: isLoadingSettings } = usePrinterSettings();
  const [presentToast, dismissToast] = useIonToast();
  const { id: eventId } = useSelector(getEventInfo);
  const isKiosk = useSelector(getKioskEnabled);
  const { push: pushRoute, routeInfo } = useIonRouter();

  const isPrinterSelected = !!settings?.printer?.serialNumber;
  const selectedPrinter = printers?.find(
    printer => printer.serialNumber === settings.printer?.serialNumber
  );

  // We only want to notify when the printer changes, but not whenever this hook is run for the
  // first time in a component, otherwise we toast too often.
  const prevSelected = useRef(selectedPrinter);

  // We only want to notify if we're not in the kiosk and not on the printer settings/selection
  const shouldToast =
    !isKiosk &&
    // Event/Details.tsx loads the event info and puts it into redux, but also renders this hook
    // at the same time, so we need to wait until the event ID is available in redux first.
    !!eventId &&
    generateRoute(RoutePath.PrinterSettings, { eventId }) !== routeInfo?.pathname &&
    generateRoute(RoutePath.PrinterSelection, { eventId }) !== routeInfo?.pathname;

  useEffect(() => {
    if (isPrinterSelected && selectedPrinter !== prevSelected.current && shouldToast) {
      const buttons: ToastButton[] = [
        {
          text: t`Printer Settings`,
          side: 'end',
          handler: () => {
            pushRoute(generateRoute(RoutePath.PrinterSettings, { eventId }));
          },
        },
      ];
      presentToast({
        message: selectedPrinter ? t`Printer Connected` : t`Printer Disconnected`,
        buttons,
        duration: 5000,
      });
    }
    return () => {
      dismissToast();
    };
  }, [
    dismissToast,
    presentToast,
    eventId,
    isPrinterSelected,
    selectedPrinter,
    pushRoute,
    shouldToast,
  ]);

  const isLoading = isLoadingPrinters || isLoadingSettings;

  return {
    enabled,
    isLoading,
    isLoadingPrinters,
    isLoadingSettings,
    availablePrinters: printers || [],
    settings,
    saveSettings,
    selectedPrinter,
    needsSetup: !enabled || !isPrinterSelected || !selectedPrinter,
  };
};
